<script>
import ResourceAttribute from '../components/ResourceAttribute.vue'
import BoardResource from '@/resources/board'
import LocationResource from '@/resources/location'
import { timeDateFormattedPretty } from '@/util'

export default {
    name: 'Leaderboards',
    components: {
        ResourceAttribute,
    },
    computed: {
        boards() {
            return this.boardResources.concat(this.boardResourcesForLocation)
        },
        board() {
            return this.selectedBoardResource
        },
        isPoints() {
            return !!this.board && this.board.attributes.calculation === 'POINTS'
        },
        boardWindowLabel() {
            if (!this.board) {
                return ''
            }
            if (this.board.attributes.since) {
                return `Since ${this.timeDateFormattedPretty(this.board.attributes.since)}`
            }
            return this.board.attributes.time_frame_pretty || 'All time'
        },
        routeState() {
            const route = { name: this.$route.name, params: { id: this.selectedBoardResource.id }, query: {} }

            if (this.selectedLocationResource) {
                route.query.location = this.selectedLocationResource.id
            }

            return route
        },
        boardFilters() {
            const filters = {}

            if (this.selectedLocationResource) {
                filters.location = this.selectedLocationResource.id
            } else {
                filters['location__isnull'] = true
            }

            return filters
        }
    },
    watch: {
        async selectedLocationResource() {
            await this.updateLocationBoards()
            await this.search()
            this.$router.replace(this.routeState)
        }
    },
    data() {
        return {
            initializing: true,
            loading: true,
            boardResources: [],
            boardResourcesForLocation: [],
            selectedBoardResource: null,
            locationResources: [],
            selectedLocationResource: null,
            rankResources: [],
        }
    },
    methods: {
        timeDateFormattedPretty,
        rankLabel(item) {
            return item.attributes.name || item.attributes.display_name
        },
        rankValue(item) {
            return this.isPoints ? `${item.attributes.points} PTS` : item.attributes.hms
        },
        rankLoc(item) {
            return item.attributes.car_name || item.attributes.car || ''
        },
        async search() {
            this.loading = true
            this.rankResources = []
            const result = await this.selectedBoardResource.getResults()
            this.rankResources = result.resources
            this.loading = false
        },
        async updateLocationBoards() {
            if (this.selectedLocationResource) {
                this.boardResourcesForLocation = await BoardResource.filterAll({ location: this.selectedLocationResource.id })
            } else {
                this.boardResourcesForLocation = []
            }
        },
        async onSelectBoard(board) {
            this.selectedBoardResource = board

            if (this.selectedBoardResource.id !== this.$route.params.id) {
                this.$router.replace(this.routeState)
            }

            if (this.selectedLocationResource) {
                await this.updateLocationBoards()
            }

            await this.search()
        },
    },
    async mounted() {
        const [locations, boards] = await Promise.all([
            LocationResource.all(),
            BoardResource.filterAll({ location__isnull: true })
        ])

        this.locationResources = locations
        this.boardResources = boards

        if (this.$route.query.location) {
            this.selectedLocationResource = this.locationResources.find((resource) => resource.id == this.$route.query.location)
        } else if (this.locationResources.length > 0) {
            this.selectedLocationResource = this.locationResources[0]
        }

        await this.updateLocationBoards()

        if (this.$route.params.id) {
            this.selectedBoardResource = this.boards.find((resource) => resource.id == this.$route.params.id)
        } else {
            this.selectedBoardResource = this.boards[0]
        }

        this.loading = false
        this.initializing = false
        await this.search()
    },
}
</script>

<template>
    <div class="lb sos-display" v-if="!initializing">
        <div class="sos-page--full">
            <header class="lb__head">
                <p class="sos-kick">{{ isPoints ? 'Meisterschaft' : 'Schnellste Runde' }} Rangliste</p>
                <h1 class="sos-h1">
                    <span class="sos-hn" v-if="board">{{ isPoints ? 'PKT.' : 'RUNDE' }}</span>{{ board ? String(board) : 'Bestenlisten' }}
                </h1>
                <p class="sos-sub" v-if="board">{{ boardWindowLabel }}</p>
            </header>

            <div class="sos-toolbar">
                <b-form-select v-if="locationResources.length > 0" v-model="selectedLocationResource" size="sm" class="lb__pick">
                    <b-form-select-option :value="null">Global</b-form-select-option>
                    <b-form-select-option v-for="location in locationResources" :key="location.id" :value="location">{{ location }}</b-form-select-option>
                </b-form-select>
                <b-form-select :value="selectedBoardResource" size="sm" class="lb__pick d-lg-none" @input="(board) => onSelectBoard(board)">
                    <b-form-select-option v-for="rowBoard in boards" :key="rowBoard.id" :value="rowBoard">{{ rowBoard }}</b-form-select-option>
                </b-form-select>
                <span class="sos-toolbar__spacer"></span>
                <b-button v-if="board && board.attributes.description" size="sm" variant="outline-secondary" @click="$refs.boardInfo.show()">Bestenlisteninfo</b-button>
            </div>

            <div class="lb__grid">
                <aside class="lb__rail d-none d-lg-block">
                    <div class="sos-lb">
                        <div class="sos-lb__h"><span>Global</span></div>
                        <button
                            v-for="rowBoard in boardResources"
                            :key="rowBoard.id"
                            class="lb__railitem"
                            :class="{ 'is-active': board && rowBoard.id === board.id }"
                            @click="onSelectBoard(rowBoard)"
                        >{{ rowBoard }}</button>
                    </div>
                    <div class="sos-lb mt-3" v-if="selectedLocationResource && boardResourcesForLocation.length > 0">
                        <div class="sos-lb__h"><span>{{ selectedLocationResource }}</span></div>
                        <button
                            v-for="rowBoard in boardResourcesForLocation"
                            :key="rowBoard.id"
                            class="lb__railitem"
                            :class="{ 'is-active': board && rowBoard.id === board.id }"
                            @click="onSelectBoard(rowBoard)"
                        >{{ rowBoard }}</button>
                    </div>
                </aside>

                <section class="lb__main">
                    <p class="lb__desc sos-sub" v-if="board && board.attributes.description" v-html="board.attributes.description"></p>

                    <div class="sos-lb" v-if="rankResources.length > 0">
                        <div class="sos-lb__h">
                            <span>Pos. / Fahrer</span>
                            <span>{{ isPoints ? 'Punkte' : 'Runde' }}</span>
                        </div>
                        <div
                            class="sos-lb__row lb__row"
                            v-for="item in rankResources"
                            :key="item.attributes.rank"
                            :class="{ 'sos-lb__row--lead': item.attributes.rank == 1 }"
                        >
                            <span class="sos-lb__pos">{{ String(item.attributes.rank).padStart(2, '0') }}</span>
                            <span class="sos-lb__who">
                                {{ rankLabel(item) }}
                                <a
                                    v-if="item.attributes.display_social"
                                    class="lb__social"
                                    :href="`https://instagram.com/${item.attributes.display_social}`"
                                    target="_blank"
                                >@{{ item.attributes.display_social }}</a>
                            </span>
                            <span class="sos-lb__loc d-none d-sm-block">{{ rankLoc(item) }}</span>
                            <span class="sos-lb__lap">{{ rankValue(item) }}</span>
                        </div>
                    </div>

                    <div class="sos-empty" v-else-if="!loading">Für diese Bestenliste wurden noch keine Runden aufgezeichnet.</div>

                    <b-skeleton-table :rows="10" v-else />
                </section>
            </div>
        </div>

        <b-modal ref="boardInfo" :title="board ? String(board) : 'Bestenlisteninfo'" hide-footer no-fade centered>
            <div v-if="board" v-html="board.attributes.description"></div>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.lb {
    min-height: 100vh;
}

.lb__head {
    display: flex;
    flex-direction: column;
    gap: 11px;
    margin-bottom: 2rem;
}

.lb__pick {
    width: auto;
    min-width: 190px;
    font-family: var(--sos-font-head);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-size: 0.82rem;
}

.lb__grid {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 1.75rem;
    align-items: start;
}
@media (max-width: 991px) {
    .lb__grid {
        grid-template-columns: 1fr;
    }
}

.lb__railitem {
    display: block;
    width: 100%;
    text-align: left;
    border: 0;
    border-bottom: 1px solid var(--sos-border);
    border-left: 3px solid transparent;
    background: transparent;
    padding: 10px 16px;
    font-family: var(--sos-font-head);
    font-weight: 600;
    font-size: 0.9rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--sos-ink-mute);
    cursor: pointer;

    &:last-child {
        border-bottom: 0;
    }
    &:hover {
        color: var(--sos-ink);
        background: var(--sos-surface-2);
    }
    &.is-active {
        color: var(--sos-teal-ink);
        border-left-color: var(--sos-teal);
        background: var(--sos-teal-soft);
    }
}

.lb__main {
    min-width: 0;
}
.lb__desc {
    margin-bottom: 1.25rem;
}

.lb__row {
    grid-template-columns: 40px 1fr auto auto;
}

.lb__social {
    font-family: var(--sos-font-mono);
    font-size: 10px;
    color: var(--sos-ink-mute);
    margin-left: 8px;
}
</style>
